import {
  useEffect,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { BlockHoverControls } from "./BlockHoverControls";
import type { BlockInsertPosition } from "./documentEditor";
import { rectEquals, rectsEqual, type OverlayRect } from "./overlayGeometry";
import {
  enclosingRenderedInstance,
  findRenderedBlockInstances,
  visiblePartRect,
  type ViewportRect,
} from "./renderedDom";
import type { DropIndicatorRect } from "./useDocumentEditor";

/**
 * Vertical gap under which two of a block's rendered instances read as one
 * box. A repeater stamps a rendered instance per row, and rows sit flush
 * against each other; outlining each one puts a doubled border in every gutter
 * instead of the single box PandaDoc draws around a whole table. A page break
 * leaves a far larger gap, so slices on different pages stay separate.
 */
const RING_MERGE_GAP_PX = 12;

interface EditorOverlayProps {
  /** Ref to the scrollable preview container the overlay is positioned in. */
  containerRef: RefObject<HTMLDivElement>;
  /** The selected block's node id, or null when nothing is selected. */
  selectedNodeId: string | null;
  /** Label shown on the selection chip. */
  label: string | null;
  /**
   * Draws a single ring rather than one per rendered instance, for a block that
   * renders on every page (the repeatable header/footer and their interiors).
   * Sliced
   * sections leave this off: their instances are parts of one block, and the
   * user wants to see every part that shows.
   */
  outlineSingleInstance: boolean;
  /**
   * Ref to the rendered instance the selection was last clicked on, so the
   * single ring lands on the copy the user actually pointed at.
   */
  clickedInstanceRef: RefObject<HTMLElement | null>;
  /** The block the pointer is over, or null when the hover is hidden. */
  hoveredNodeId: string | null;
  /** Draws one hover ring for an every-page block, like the selection. */
  hoverOutlineSingleInstance: boolean;
  /** The block the hover controls act on: the hovered block, or its table. */
  hoverChromeNodeId: string | null;
  /** Ref to the rendered instance the hover last resolved to. */
  hoveredInstanceRef: RefObject<HTMLElement | null>;
  /** True while the hovered block is being drag-reordered. */
  isDragging: boolean;
  /** Starts a drag-reorder session for the block the controls act on. */
  onDragStart: (nodeId: string, event: ReactPointerEvent<HTMLElement>) => void;
  /** Drop indicator geometry while drag-reordering, or null. */
  dropIndicator: DropIndicatorRect | null;
  /** Opens the block palette to insert beside the hovered block. */
  onAddContent: (
    nodeId: string,
    position: BlockInsertPosition,
    anchorRect: OverlayRect,
  ) => void;
}

/**
 * Selection and hover chrome for the WYSIWYG preview: blue rings with a label
 * chip hugging the selected block's VISIBLE rendered geometry, the controls
 * that appear beside the hovered block (its reorder grip, and a "+" per edge
 * that opens the palette to insert there), and the drag-reorder drop indicator
 * line. A block inside a sliced section renders
 * once per slice page (each copy clipped by its page's viewport), so the
 * overlay draws one ring per rendered instance that actually shows, each
 * clipped to its visible part - never around an invisible slice copy on
 * another page. A selected repeater draws its rings around the whole repeater
 * block, since its stamped instances are opaque. The repeatable header and
 * footer are the other multi-instance case, and the opposite one: each renders
 * in full on every page, so `outlineSingleInstance` narrows it to one ring -
 * the copy the user clicked - instead of running rings down the whole
 * document. Hovering a block previews the same ring faded out, resolved the
 * way a click resolves it, so what the pointer is over is what a click would
 * select - one ring per visible slice for a cross-page block, narrowed to the
 * pointed copy for an every-page block, and never on the block already
 * selected. The controls, though, hang off
 * whatever `hoverChromeNodeId` names: hovering a table column rings the column
 * (a click selects it) but puts the grip and the "+" buttons on the whole
 * table, which is the block they can actually move and insert beside - on the
 * copy of that table the pointer is inside, so a table split across pages keeps
 * its controls on the page being hovered. Rings and controls both track the
 * rendered DOM with one requestAnimationFrame loop (re-resolving by
 * data-fp-node each frame), so they follow scrolling, re-renders, and async
 * pagination without extra wiring. The chip sits on the topmost ring.
 * @param containerRef - The scrollable preview container
 * @param selectedNodeId - The selected block's node id
 * @param label - Selection chip label
 * @param outlineSingleInstance - Draws one ring for an every-page block
 * @param clickedInstanceRef - The instance the selection was clicked on
 * @param hoveredNodeId - The block the pointer is over
 * @param hoverOutlineSingleInstance - Draws one hover ring for an every-page block
 * @param hoverChromeNodeId - The block the hover controls act on
 * @param hoveredInstanceRef - The instance the hover resolved to
 * @param isDragging - True while the hovered block is being dragged
 * @param onDragStart - Starts a drag-reorder session for the hovered block
 * @param dropIndicator - Drop indicator geometry while dragging
 * @param onAddContent - Opens the block palette for the hovered block
 */
export function EditorOverlay({
  containerRef,
  selectedNodeId,
  label,
  outlineSingleInstance,
  clickedInstanceRef,
  hoveredNodeId,
  hoverOutlineSingleInstance,
  hoverChromeNodeId,
  hoveredInstanceRef,
  isDragging,
  onDragStart,
  dropIndicator,
  onAddContent,
}: EditorOverlayProps) {
  const [rects, setRects] = useState<OverlayRect[]>([]);
  const [hoveredRects, setHoveredRects] = useState<OverlayRect[]>([]);
  const [chromeRect, setChromeRect] = useState<OverlayRect | null>(null);

  useEffect(() => {
    if (!selectedNodeId && !hoveredNodeId) {
      setRects([]);
      setHoveredRects([]);
      setChromeRect(null);
      return;
    }
    let frame = 0;
    const track = () => {
      const container = containerRef.current;
      const next =
        container && selectedNodeId
          ? resolveSelectionRects(
              container,
              selectedNodeId,
              outlineSingleInstance,
              clickedInstanceRef.current,
            )
          : [];
      setRects((previous) => (rectsEqual(previous, next) ? previous : next));
      const nextHovered =
        container && hoveredNodeId
          ? resolveSelectionRects(
              container,
              hoveredNodeId,
              hoverOutlineSingleInstance,
              hoveredInstanceRef.current,
            )
          : [];
      setHoveredRects((previous) =>
        rectsEqual(previous, nextHovered) ? previous : nextHovered,
      );
      const nextChrome =
        !container || !hoverChromeNodeId
          ? null
          : resolveInstanceRect(
              container,
              hoverChromeNodeId,
              hoverChromeNodeId === hoveredNodeId
                ? hoveredInstanceRef.current
                : enclosingRenderedInstance(
                    hoveredInstanceRef.current,
                    hoverChromeNodeId,
                  ),
            );
      setChromeRect((previous) =>
        rectEquals(previous, nextChrome) ? previous : nextChrome,
      );
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frame);
  }, [
    clickedInstanceRef,
    containerRef,
    hoverChromeNodeId,
    hoverOutlineSingleInstance,
    hoveredInstanceRef,
    hoveredNodeId,
    outlineSingleInstance,
    selectedNodeId,
  ]);

  return (
    <>
      {hoveredNodeId !== selectedNodeId &&
        hoveredRects.map((rect, index) => (
          <div
            key={`hover-ring-${index}`}
            className="absolute z-30 pointer-events-none outline outline-2 outline-offset-[1px] outline-blue-500/30 rounded-[4px]"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        ))}
      {rects.map((rect, index) => (
        <div
          key={`selection-ring-${index}`}
          className="absolute z-40 pointer-events-none outline outline-2 outline-offset-[1px] outline-blue-500 rounded-[4px]"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          {label && index === 0 && (
            <span className="absolute -top-[18px] -left-[2px] max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap rounded-t bg-blue-500 px-1.5 text-[10px] font-medium leading-[18px] text-white">
              {label}
            </span>
          )}
        </div>
      ))}
      {hoverChromeNodeId && chromeRect && (
        <BlockHoverControls
          blockRect={chromeRect}
          isDragging={isDragging}
          onDragStart={(event) => onDragStart(hoverChromeNodeId, event)}
          onAdd={(position, anchorRect) =>
            onAddContent(hoverChromeNodeId, position, anchorRect)
          }
        />
      )}
      {dropIndicator && (
        <div
          className="absolute z-50 pointer-events-none h-[3px] rounded-full bg-blue-500 shadow-[0_0_0_2px_rgba(255,255,255,0.9)]"
          style={{
            top: dropIndicator.top - 1,
            left: dropIndicator.left,
            width: dropIndicator.width,
          }}
        />
      )}
    </>
  );
}

/** A rendered instance of a block paired with its visible part. */
interface VisibleInstance {
  instance: HTMLElement;
  rect: ViewportRect;
}

/**
 * Resolves the container-relative outline rects for the current selection:
 * one rect per rendered instance that has any visible geometry, each clipped
 * to the part the user can see, sorted top to bottom - or a single rect when
 * the block renders on every page.
 * @param container - The scrollable preview container
 * @param nodeId - The selected block's node id
 * @param outlineSingleInstance - Narrows the result to one instance
 * @param clickedInstance - The instance the selection was clicked on, or null
 * @returns The outline rects, empty when nothing visible resolves
 */
function resolveSelectionRects(
  container: HTMLElement,
  nodeId: string,
  outlineSingleInstance: boolean,
  clickedInstance: HTMLElement | null,
): OverlayRect[] {
  const visible = visibleInstances(container, nodeId);
  const outlined = outlineSingleInstance
    ? pickOneInstance(visible, clickedInstance)
    : visible;
  return mergeTouchingRects(
    outlined
      .map(({ rect }) => rect)
      .sort((a, b) => a.top - b.top)
      .map((rect) => toOverlayRect(container, rect)),
  );
}

/**
 * Folds rects that sit against each other into their bounding box, so a block
 * whose rows each render as their own instance outlines as one box.
 * @param rects - The instance rects, sorted top to bottom
 * @returns One rect per run of touching instances
 */
function mergeTouchingRects(rects: OverlayRect[]): OverlayRect[] {
  return rects.reduce<OverlayRect[]>((merged, rect) => {
    const last = merged.at(-1);
    if (!last || rect.top - (last.top + last.height) > RING_MERGE_GAP_PX) {
      return [...merged, rect];
    }
    const left = Math.min(last.left, rect.left);
    const top = Math.min(last.top, rect.top);
    const right = Math.max(last.left + last.width, rect.left + rect.width);
    const bottom = Math.max(last.top + last.height, rect.top + rect.height);
    return [
      ...merged.slice(0, -1),
      { top, left, width: right - left, height: bottom - top },
    ];
  }, []);
}

/**
 * Resolves the container-relative rect of the one instance chrome should hang
 * off: the copy the pointer resolved to when it is still visible, otherwise the
 * first visible copy.
 * @param container - The scrollable preview container
 * @param nodeId - The block's node id
 * @param instance - The instance the pointer resolved to, or null
 * @returns The rect, or null when the block has no visible instance
 */
function resolveInstanceRect(
  container: HTMLElement,
  nodeId: string,
  instance: HTMLElement | null,
): OverlayRect | null {
  const [visible] = pickOneInstance(
    visibleInstances(container, nodeId),
    instance,
  );
  return visible ? toOverlayRect(container, visible.rect) : null;
}

/**
 * Lists a block's rendered instances that have any visible geometry, paired
 * with the part of each the user can actually see.
 * @param container - The scrollable preview container
 * @param nodeId - The block's node id
 * @returns The visible instances, in DOM order
 */
function visibleInstances(
  container: HTMLElement,
  nodeId: string,
): VisibleInstance[] {
  return findRenderedBlockInstances(container, nodeId)
    .map((instance) => ({
      instance,
      rect: visiblePartRect(container, instance),
    }))
    .filter(
      (candidate): candidate is VisibleInstance => candidate.rect !== null,
    );
}

/**
 * Narrows a block's visible instances to the one to draw chrome on: the copy
 * the user pointed at when it is still among them, otherwise the first one.
 * Testing membership rather than trusting the ref also covers the cases where
 * the pointer hint is stale - the selection moved on through the breadcrumb, or
 * the copy was re-rendered away.
 * @param visible - The block's visible rendered instances, in DOM order
 * @param pointed - The instance the pointer resolved to, or null
 * @returns The single instance to draw on, empty when none are visible
 */
function pickOneInstance(
  visible: VisibleInstance[],
  pointed: HTMLElement | null,
): VisibleInstance[] {
  const match = visible.find((candidate) => candidate.instance === pointed);
  return match ? [match] : visible.slice(0, 1);
}

/**
 * Converts a viewport rect into the preview container's scroll coordinates.
 * @param container - The scrollable preview container
 * @param rect - The rect in viewport coordinates
 * @returns The container-relative rect
 */
function toOverlayRect(
  container: HTMLElement,
  rect: ViewportRect,
): OverlayRect {
  const containerRect = container.getBoundingClientRect();
  return {
    top: rect.top - containerRect.top + container.scrollTop,
    left: rect.left - containerRect.left + container.scrollLeft,
    width: rect.right - rect.left,
    height: rect.bottom - rect.top,
  };
}
